import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { formatDateTime } from "../../../helpers/cookies";
import { download_file } from "../../../store/job/jobSlice";
import { useDispatch } from "react-redux";

const ProofLogTable = ({ proof_log_data }) => {
  const dispatch = useDispatch();

  const columns = [
    "Proof #",
    "Proof Sent Datetime",
    "Sent To",
    "CC",
    "Filename",
    "Feedback",
    "Approve/Reject Datetime",
    "Action",
  ];

  const fontSize = Math.max(0.75, 1.2 - columns.length * 0.05) + "rem";

  const getStatusText = (status) => {
    switch (status) {
      case 1: return "Sent to Customer";
      case 2: return "Approved";
      case 3: return "Rejected";
      case 4: return "Cancelled";
      default: return "-";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return "#1204d6ff";
      case 2: return "green";
      case 3: return "red";
      case 4: return "gray";
      default: return "black";
    }
  };

  const extractJobId = (fileKey) => {
    const parts = fileKey.split("/");
    const numericParts = parts.filter((p) => /^\d+$/.test(p));
    const jobId = numericParts.length > 0 ? numericParts[numericParts.length - 1] : null;
    return jobId ? parseInt(jobId, 10) : null;
  };

  const handleDownloadFile = async (fileKey) => {
    try {
      const jobId = extractJobId(fileKey);
      if (!jobId) return;
      const action = await dispatch(download_file({ job_id: jobId, file_key: fileKey, d_type: "" }));
      if (download_file.fulfilled.match(action)) {
        const url = action.payload.data.url;
        const link = document.createElement("a");
        link.href = url;
        link.download = fileKey.split("/").pop();
        link.click();
      } else {
        console.error("Failed to download file", action.payload);
      }
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ boxShadow: 3, borderRadius: 2, overflow: "hidden" }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#F5F5F5" }}>
            {columns.map((head, idx) => (
              <TableCell
                key={idx}
                sx={{
                  color: "black",
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize,
                }}
              >
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {proof_log_data.length > 0 ? (
            proof_log_data.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:nth-of-type(odd)": { bgcolor: "#f9faff" },
                  "&:hover": { bgcolor: "#edf2ff" },
                }}
              >
                <TableCell align="center" sx={{ fontSize, width: "80px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {row.id}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "140px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {formatDateTime(row.proof_sent_date)}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "150px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {row.sent_to}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "220px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {row.sent_to_cc || "-"}
                </TableCell>

                <TableCell
                  align="center"
                  sx={{ fontSize, width: "180px", wordBreak: "break-word", whiteSpace: "normal", color: "#0d6efd", cursor: "pointer" }}
                  onClick={() => handleDownloadFile(row.file_path)}
                >
                  {row.file_path.split("/").pop()}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "150px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {row.feedbacks}
                  {row.feedbacks && row.view_proof_ids && <br />}
                  {row.view_proof_ids && (
                    <a
                      href={`/viewproof/?proof_id=${row.view_proof_ids}&auth_user=${row.sent_by}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0d6efd", textDecoration: "none" }}
                    >
                      View Proof
                    </a>
                  )}
                  {!row.feedbacks && !row.view_proof_ids && "-"}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "140px", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {row.approve_reject_date ? formatDateTime(row.approve_reject_date) : "-"}
                </TableCell>

                <TableCell align="center" sx={{ fontSize, width: "100px", wordBreak: "break-word", whiteSpace: "normal", color: getStatusColor(row.sent_approved_files) }}>
                  {getStatusText(row.sent_approved_files)}
                </TableCell>
              </TableRow>

            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography align="center" sx={{ py: 2, color: "gray", fontSize }}>
                  No data available in Proof Log.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProofLogTable;